import React, { useState, useEffect, useRef } from 'react';
import { Plus } from 'lucide-react';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import SessionPropertyFilterModule from './SessionPropertyFilterModule';
import SessionFilterChip from './SessionFilterChip';
import TransactionPropertyFilterChip from './TransactionPropertyFilterChip';

/**
 * Sessions filter module: same UX as Events/Transactions global filters.
 * One FILTERS section with Add, chips (WHERE/AND/OR), 4-tab property picker (Sessions, User, Event, All).
 */
const SessionsFilterModule = ({
  filters = [],
  onFiltersChange,
  teamId
}) => {
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState(null);
  const [fieldOptions, setFieldOptions] = useState({});
  const addButtonRef = useRef(null);

  // Only fetch filter options when user is adding/editing a filter or has existing filters (needed for chip dropdowns).
  // This avoids heavy /sessions and sessions/countries|pages|operating-systems calls when just viewing Offchain or when on Events tab.
  const needsFilterOptions = isAddPopupOpen || editingFilter || (filters && filters.length > 0);

  useEffect(() => {
    if (!needsFilterOptions || !teamId) return;
    const fetchOptions = async () => {
      try {
        const params = new URLSearchParams({ page: '1', limit: '500' });
        params.set('teamId', teamId);
        const res = await axiosInstance.get(`/sessions?${params.toString()}`);
        const sessions = res.data.sessions || [];
        const filterVal = (v) => v != null && String(v).trim() !== '';
        const cities = [...new Set(sessions.map((s) => s.locationData?.city).filter(filterVal))].sort();
        const browsers = [...new Set(sessions.map((s) => s.browser?.name).filter(filterVal))].sort();
        const entryPages = [...new Set(sessions.map((s) => s.entryPage).filter(filterVal))].sort();
        const exitPages = [...new Set(sessions.map((s) => s.exitPage).filter(filterVal))].sort();
        const referrers = [...new Set(sessions.map((s) => s.referrer).filter(filterVal))].sort();
        const deviceTypes = [...new Set(sessions.map((s) => s.device?.type).filter(filterVal))].sort();
        setFieldOptions((prev) => ({
          ...prev,
          city: cities,
          browser: browsers,
          entryPage: entryPages,
          exitPage: exitPages,
          referrer: referrers,
          deviceType: deviceTypes
        }));
      } catch (e) {
        console.warn('Sessions filter: failed to fetch session options', e);
      }
    };
    fetchOptions();
  }, [needsFilterOptions, teamId]);

  useEffect(() => {
    if (!needsFilterOptions || !teamId) return;
    const q = new URLSearchParams({ teamId });
    const endpoints = [
      { key: 'country', url: `/sessions/team/countries?${q.toString()}` },
      { key: 'pages', url: `/sessions/team/pages?${q.toString()}` },
      { key: 'operatingSystem', url: `/sessions/team/operating-systems?${q.toString()}` }
    ];
    endpoints.forEach(({ key, url }) => {
      axiosInstance
        .get(url)
        .then((r) => {
          let list = [];
          if (key === 'country' && r.data?.countries) list = (r.data.countries.map((c) => c.country || c)).filter(Boolean).sort();
          if (key === 'pages' && r.data?.pages) list = (r.data.pages.map((p) => p.path || p)).filter(Boolean).sort();
          if (key === 'operatingSystem' && r.data?.operatingSystems) list = (r.data.operatingSystems.map((o) => o.os || o)).filter(Boolean).sort();
          setFieldOptions((prev) => ({ ...prev, [key]: list }));
        })
        .catch((err) => console.warn(`Sessions filter: failed to fetch ${key}`, err));
    });
  }, [needsFilterOptions, teamId]);

  const handleAddFilter = () => {
    setEditingFilter(null);
    setIsAddPopupOpen(true);
  };

  const handlePropertyAdd = (newFilter) => {
    const connector = filters.length === 0 ? 'WHERE' : (filters.some((f) => f.id === newFilter.id) ? (filters.find((f) => f.id === newFilter.id)?.connector || 'AND') : 'AND');
    const withConnector = { ...newFilter, connector };
    if (filters.some((f) => f.id === newFilter.id)) {
      onFiltersChange(filters.map((f) => (f.id === newFilter.id ? withConnector : f)));
    } else {
      onFiltersChange([...filters, withConnector]);
    }
    setIsAddPopupOpen(false);
    setEditingFilter(null);
  };

  const handleFilterUpdate = (filterId, updates) => {
    onFiltersChange(filters.map((f) => (f.id === filterId ? { ...f, ...updates } : f)));
  };

  const handleFilterRemove = (filterId) => {
    const next = filters.filter((f) => f.id !== filterId).map((f, i) => ({ ...f, connector: i === 0 ? 'WHERE' : 'AND' }));
    onFiltersChange(next);
    if (editingFilter?.id === filterId) {
      setEditingFilter(null);
      setIsAddPopupOpen(false);
    }
  };

  const handleEditFilter = (filter) => {
    setEditingFilter(filter);
    setIsAddPopupOpen(true);
  };

  return (
    <div className="bg-transparent border border-dotted border-gray-300 rounded-lg overflow-hidden" style={{ fontFamily: 'Raleway, sans-serif' }}>
      <div className="bg-transparent overflow-hidden">
        <div className="pl-4 pr-6 pt-2 pb-3 flex items-center justify-between border-b border-dotted border-gray-300">
          <h3 className="text-xs font-bold uppercase tracking-wide text-[#0f172a]">
            FILTERS
          </h3>
        </div>
        <div className="p-6">
          {filters.length > 0 && (
            <div className="space-y-2 mb-4">
              {filters.map((filter, index) => {
                const isFirst = index === 0;
                const category = filter.category || filter.propertySource || 'session';
                if (category === 'session') {
                  return (
                    <SessionFilterChip
                      key={filter.id}
                      filter={filter}
                      isFirst={isFirst}
                      onEdit={() => handleEditFilter(filter)}
                      onUpdate={(updates) => handleFilterUpdate(filter.id, updates)}
                      onRemove={() => handleFilterRemove(filter.id)}
                      fieldOptions={fieldOptions}
                    />
                  );
                }
                return (
                  <TransactionPropertyFilterChip
                    key={filter.id}
                    filter={{
                      ...filter,
                      propertySource: filter.propertySource || filter.category,
                      property: filter.property || filter.field,
                      propertyLabel: filter.propertyLabel,
                      propertyType: filter.propertyType
                    }}
                    isFirst={isFirst}
                    teamId={teamId}
                    onEdit={() => handleEditFilter(filter)}
                    onUpdate={(updates) => handleFilterUpdate(filter.id, updates)}
                    onRemove={() => handleFilterRemove(filter.id)}
                  />
                );
              })}
            </div>
          )}
          <div>
            <button
              ref={addButtonRef}
              type="button"
              onClick={handleAddFilter}
              className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#0f172a] transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add</span>
            </button>
          </div>
        </div>
      </div>

      {filters.length > 0 && (
        <div className="px-6 pb-2">
          <button
            type="button"
            onClick={() => onFiltersChange([])}
            className="text-sm text-[#475569] hover:text-[#0f172a]"
          >
            Clear all
          </button>
        </div>
      )}

      {(isAddPopupOpen || editingFilter) && (
        <SessionPropertyFilterModule
          isOpen={isAddPopupOpen || !!editingFilter}
          onClose={() => {
            setIsAddPopupOpen(false);
            setEditingFilter(null);
          }}
          triggerRef={addButtonRef}
          teamId={teamId}
          filters={filters}
          existingFilter={editingFilter}
          onAdd={handlePropertyAdd}
        />
      )}
    </div>
  );
};

export default SessionsFilterModule;
